import React, {
    useEffect,
    useState
} from "react";

import axios from "axios";

import {
    CheckCircle2,
    Circle,
    ArrowRight,
    Target,
    Bot,
    Upload,
    Check
} from "lucide-react";

import { useNavigate } from "react-router-dom";

import "./LevelObjectivesCard.css";

const defaultObjectives = {
    currentLevel: 1,
    completed: 0,
    total: 0,
    progress: 0,
    tasks: []
};

const manualTasks = [
    "Join Community",
    "Ask Your First Question",
    "Attend Community Call"
];

const uploadTasks = [
    "Upload Profile Photo",
    "Upload Hammer 150",
    "Upload Hammer 150 (Non Dominant)",
    "Upload Real Life Story",
    "Upload Sleep Tracking"
];

const LevelObjectivesCard = () => {

    const navigate = useNavigate();

    const email = localStorage.getItem("user_email");

    const [objectives, setObjectives] =
        useState(defaultObjectives);

    const [loading, setLoading] =
        useState(true);

    useEffect(() => {

        if (email) {
            fetchObjectives();
        } else {
            setLoading(false);
        }

    }, [email]);

    const fetchObjectives = async () => {

        try {

            const res = await axios.get(
                `/api/ignite/journey/${email}`
            );

            if (
                res.data?.success &&
                res.data?.levelObjectives
            ) {

                setObjectives({
                    ...defaultObjectives,
                    ...res.data.levelObjectives,
                    tasks:
                        res.data.levelObjectives.tasks || []
                });

            } else {

                setObjectives(defaultObjectives);

            }

        }
        catch (err) {

            console.error(
                "Journey API Error:",
                err
            );

            setObjectives(defaultObjectives);

        }
        finally {

            setLoading(false);

        }

    };

    // =====================================================
    // MANUAL TASK UPDATE
    // =====================================================

    const updateManualTask = async (title) => {

        try {

            let field = "";

            switch (title) {

                case "Join Community":
                    field = "communityJoined";
                    break;

                case "Ask Your First Question":
                    field = "askedFirstQuestion";
                    break;

                case "Attend Community Call":
                    field = "attendedCommunityCalls";
                    break;

                default:
                    return;
            }

            await axios.patch(
                "/api/ignite/update",
                {
                    email,
                    field,
                    value: true
                }
            );

            fetchObjectives();

        }
        catch (err) {

            console.error(err);

        }

    };

    // =====================================================
    // UPLOAD TASK NAVIGATION
    // =====================================================

    const handleUploadTask = (taskTitle) => {

        switch (taskTitle) {

            case "Upload Profile Photo":
    navigate("/ignite/profile");
    break;

case "Upload Hammer 150":
case "Upload Hammer 150 (Non Dominant)":
case "Upload Real Life Story":
case "Upload Sleep Tracking":
    navigate("/ignite/upload");
    break;
            default:

                console.warn(
                    "No upload route found for:",
                    taskTitle
                );

        }

    };

    // =====================================================
    // LOADING
    // =====================================================

    if (loading) {

        return (

            <div className="level-objectives-card loading">

                Loading Objectives...

            </div>

        );

    }

    return (

        <div className="level-objectives-card">

            {/* =====================================================
                HEADER
            ===================================================== */}

            <div className="level-objectives-header">

                <div>

                    <h2>

                        Level {objectives.currentLevel} Objectives

                    </h2>

                    <p>

                        Complete all objectives to unlock the next level.

                    </p>

                </div>

                <Target
                    className="header-icon"
                    size={32}
                />

            </div>


            {/* =====================================================
                TASK LIST
            ===================================================== */}

            <div className="level-task-list">

                {

                    objectives.tasks.length === 0

                    ?

                    (

                        <div className="empty-objectives">

                            No objectives available.

                        </div>

                    )

                    :

                    (

                        objectives.tasks.map(

                            (task, index) => {

                                const isManual =
                                    manualTasks.includes(
                                        task.title
                                    );

                                const isUpload =
                                    uploadTasks.includes(
                                        task.title
                                    );

                                const isAuto =
                                    !isManual &&
                                    !isUpload;

                                return (

                                    <div
                                        key={index}
                                        className={`level-task ${
                                            task.completed
                                                ? "completed"
                                                : ""
                                        }`}
                                    >

                                        {/* =====================================================
                                            LEFT SIDE
                                        ===================================================== */}

                                        <div className="task-left">

                                            {

                                                task.completed

                                                ?

                                                <CheckCircle2
                                                    size={22}
                                                />

                                                :

                                                <Circle
                                                    size={22}
                                                />

                                            }

                                            <span>

                                                {task.title}

                                            </span>

                                        </div>


                                        {/* =====================================================
                                            RIGHT SIDE
                                        ===================================================== */}

                                        <div className="task-right">

                                            {/* COMPLETED */}

                                            {

                                                task.completed

                                                ?

                                                (

                                                    <span className="completed-badge">

                                                        <Check
                                                            size={14}
                                                        />

                                                        Completed

                                                    </span>

                                                )

                                                :

                                                /* MANUAL TASK */

                                                isManual

                                                ?

                                                (

                                                    <button
                                                        className="manual-btn"
                                                        onClick={() =>
                                                            updateManualTask(
                                                                task.title
                                                            )
                                                        }
                                                    >

                                                        Mark Done

                                                    </button>

                                                )

                                                :

                                                /* UPLOAD TASK */

                                                isUpload

                                                ?

                                                (

                                                    <button
                                                        className="upload-btn"
                                                        onClick={() =>
                                                            handleUploadTask(
                                                                task.title
                                                            )
                                                        }
                                                    >

                                                        <Upload
                                                            size={15}
                                                        />

                                                        Upload

                                                    </button>

                                                )

                                                :

                                                /* AUTO TASK */

                                                (

                                                    <span className="auto-badge">

                                                        <Bot
                                                            size={14}
                                                        />

                                                        Auto

                                                    </span>

                                                )

                                            }

                                        </div>

                                    </div>

                                );

                            }

                        )

                    )

                }

            </div>


            {/* =====================================================
                PROGRESS
            ===================================================== */}

            <div className="objective-progress">

                <div className="progress-info">

                    <span>

                        Progress

                    </span>

                    <span>

                        {objectives.completed} /
                        {objectives.total}

                    </span>

                </div>

                <div className="progress-bar">

                    <div
                        className="progress-fill"
                        style={{
                            width: `${objectives.progress}%`
                        }}
                    />

                </div>

            </div>


            {/* =====================================================
                LEVEL COMPLETE
            ===================================================== */}

            {

                objectives.progress >= 100

                &&

                (

                    <div className="next-level-card">

                        <div>

                            <h4>

                                🎉 Level Complete!

                            </h4>

                            <p>

                                You're ready to unlock the next level.

                            </p>

                        </div>

                        <button
                            className="next-level-btn"
                            onClick={() =>
                                navigate(
                                    "/ignite/dashboard"
                                )
                            }
                        >

                            Continue

                            <ArrowRight
                                size={18}
                            />

                        </button>

                    </div>

                )

            }

        </div>

    );

};

export default LevelObjectivesCard;